import type { components } from '@st/api/paths'

export type BaseSortTypeGames =
  | 'gameId'
  | 'createdAt'
  | 'rtp'
  | 'popularity'
  | 'weightInCategory'
  | 'name'

interface BaseCasinoGamesSortRule {
  fieldName: Exclude<BaseSortTypeGames, 'rtp'>
  sortOrder: components['schemas']['SortOrder']
}

export type CasinoGamesSortRule =
  | BaseCasinoGamesSortRule
  | {
      fieldName: Extract<BaseSortTypeGames, 'rtp'>
      sortOrder: BaseCasinoGamesSortRule['sortOrder']
      nullsLast: boolean
    }

export type SortTypeGames =
  | Exclude<BaseSortTypeGames, 'name' | 'gameId'>
  | 'nameAsc'
  | 'nameDesc'

const NAME_ASCENDING_SORT: CasinoGamesSortRule = {
  fieldName: 'name',
  sortOrder: 'ASC',
}

export function useSelectSorting(
  initialSort: SortTypeGames = 'weightInCategory',
) {
  const { t } = useI18n()

  const sort = ref<SortTypeGames>(initialSort)

  const orderBy = computed<CasinoGamesSortRule[]>(() => {
    switch (sort.value) {
      case 'nameAsc':
        return [NAME_ASCENDING_SORT]
      case 'nameDesc':
        return [
          {
            fieldName: 'name',
            sortOrder: 'DESC',
          } as const,
        ]
      case 'rtp':
        return [
          {
            fieldName: 'rtp',
            sortOrder: 'DESC',
            nullsLast: true,
          },
          NAME_ASCENDING_SORT,
        ]
      default:
        return [
          {
            fieldName: sort.value,
            sortOrder: 'DESC',
          } as const,
          NAME_ASCENDING_SORT,
        ]
    }
  })

  const sortOptions = computed<{ id: string; title: string }[]>(() => [
    { id: 'weightInCategory', title: t('casinoSort.weight') },
    { id: 'popularity', title: t('casinoSort.popular') },
    { id: 'rtp', title: t('casinoSort.rtp') },
    { id: 'createdAt', title: t('casinoSort.createdAt') },
    { id: 'nameAsc', title: t('casinoSort.nameAsc') },
    { id: 'nameDesc', title: t('casinoSort.nameDesc') },
  ])

  return { sort, orderBy, sortOptions }
}
